import { useEffect, useRef, useState } from 'react';
import { debounce } from '../domain/utils/debounce';
import { throttle } from '../domain/utils/throttle';

const getScrollCoordinates = () => {
  return {
    x: window.scrollX,
    y: window.scrollY
  };
};

export const useDoesWindowScrollMeetsCriteria = (
  /*
  Criteria should be a constant function to avoid passing
  a new function every time the consumer component re-renders.
  If it is impossible to use a constant function, you should
  define criteria as a dependency of the effect below.
  */
  criteria,
  {
    initialValue = false,
    once = false,
    throttleWait = 0,
    debounceWait = 0
  } = {}
) => {
  const [meetsCriteria, setMeetsCriteria] = useState(initialValue);
  const ref = useRef({
    previousResult: initialValue,
    enabled: true
  });

  const handleEventRaw = () => {
    // When criteria is met, this hook set state, which could modify the DOM,
    // triggering a scroll or resize which would execute this handle again
    // before it can be disabled. Hence, always check id this hook is enabled or not.
    if (ref.current.enabled) {
      const scrollCoordinates = getScrollCoordinates();
      const criteriaResult = criteria(scrollCoordinates);
      if (criteriaResult !== ref.current.previousResult) {
        ref.current = {
          previousResult: criteriaResult,
          enabled: !once
        };
        // We cannot use setMeetsCriteria and set the same
        // value again and again due to React state being asynchronous.
        setMeetsCriteria(criteriaResult);
      }
    }
  };

  let handleEvent = handleEventRaw;
  if (throttleWait) {
    handleEvent = throttle(handleEventRaw, throttleWait);
  }
  if (debounceWait) {
    handleEvent = debounce(handleEventRaw, debounceWait);
  }

  const removeEventListeners = () => {
    window.removeEventListener('scroll', handleEvent, {
      passive: true
    });
    window.removeEventListener('resize', handleEvent, {
      passive: true
    });
  };

  useEffect(() => {
    // Execute handleEvent before setting up event listeners,
    // to check if criteria is met before a scroll or resize event happens.
    handleEvent();
    // If criteria executed in handleEvent is met, and "once" option is true,
    // this hooks is disabled and there is no need to set up any listener.
    if (ref.current.enabled) {
      window.addEventListener('scroll', handleEvent, {
        passive: true
      });
      window.addEventListener('resize', handleEvent, {
        passive: true
      });
    } else {
      removeEventListeners();
    }
    return () => {
      removeEventListeners();
    };
  }, []);

  return meetsCriteria;
};
